import { useMutation } from '@apollo/client';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useCompleteFragment } from '@eluve/apollo-client';
import {
  type TenantBundle,
  appointmentTemplateBundleFragment,
  updateAppointmentTemplateBundleMutation,
  useAppointmentContext,
  useTenantBundles,
} from '@eluve/frontend-appointment-hooks';

/**
 * Sentinel value used by bundle selectors to represent the "Default" row.
 * Lives here because both BundleTemplateSelector and AppointmentSettingsDropdown
 * need to recognize it.
 */
export const BUNDLE_DEFAULT_SENTINEL = '__default__';

type AppointmentBundleContextValue = {
  storedBundleId: string | null;
  computedDefaultBundleName: string | null;
  hasComputedDefault: boolean;
  bundles: TenantBundle[];
  loading: boolean;
  updateTemplateBundleId: (bundleId: string) => Promise<unknown>;
  clearToDefault: () => Promise<unknown>;
};

const AppointmentBundleContext = createContext<
  AppointmentBundleContextValue | undefined
>(undefined);

export const useAppointmentBundleContext =
  (): AppointmentBundleContextValue => {
    const context = useContext(AppointmentBundleContext);
    if (!context) {
      throw new Error(
        'useAppointmentBundleContext must be used inside an AppointmentBundleProvider',
      );
    }
    return context;
  };

type AppointmentBundleProviderProps = {
  children: ReactNode;
};

export const AppointmentBundleProvider: React.FC<
  AppointmentBundleProviderProps
> = ({ children }) => {
  const { appointmentId, tenantId } = useAppointmentContext();
  const apiClient = useApiClient();

  const { bundles, loading } = useTenantBundles(tenantId);

  const appointment = useCompleteFragment({
    fragment: appointmentTemplateBundleFragment,
    key: { id: appointmentId },
    strict: false,
  });

  const storedBundleId =
    (appointment?.initialTemplateBundleId as string | null) ?? null;

  const [computedDefaultBundleId, setComputedDefaultBundleId] = useState<
    string | null
  >(null);

  useEffect(
    function resolveDefaultBundle() {
      if (!tenantId || !appointmentId) {
        return;
      }

      // Clear any value resolved for a previous appointment so consumers do
      // not render a stale `Default:` label during the new in-flight call.
      setComputedDefaultBundleId(null);

      let cancelled = false;
      void (async () => {
        try {
          const response =
            await apiClient.templateBundleDefaults.resolveForAppointment({
              params: { tenantId, appointmentId },
            });
          if (cancelled) return;
          if (response.status === 200) {
            setComputedDefaultBundleId(response.body.bundleId);
          } else {
            setComputedDefaultBundleId(null);
          }
        } catch {
          if (!cancelled) {
            setComputedDefaultBundleId(null);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    [apiClient, tenantId, appointmentId],
  );

  // Defensive guard: if the resolver returns a bundle id that isn't in the
  // tenant's loaded enabled-bundles list, drop it. The backend resolver
  // already filters by isEnabled at every tier, so this should never fire
  // — but a stale id (e.g. bundle disabled between resolve and render) would
  // otherwise leave us showing "Default -" with no name. Wait until `loading`
  // is false so we don't transiently null out a valid id mid-fetch.
  const resolvedBundle = useMemo(() => {
    if (!computedDefaultBundleId) return null;
    if (loading) return undefined;
    return bundles.find((b) => b.id === computedDefaultBundleId) ?? null;
  }, [bundles, computedDefaultBundleId, loading]);

  const effectiveComputedDefaultBundleId =
    resolvedBundle === null ? null : computedDefaultBundleId;
  const computedDefaultBundleName = resolvedBundle?.displayName ?? null;

  const [updateBundleMutation] = useMutation(
    updateAppointmentTemplateBundleMutation,
  );

  const updateTemplateBundleId = useCallback(
    (bundleId: string) => {
      const bundle = bundles.find((b) => b.id === bundleId);
      return updateBundleMutation({
        variables: {
          tenantId: tenantId!,
          id: appointmentId,
          initialTemplateBundleId: bundleId,
          initialPromptTemplateId: bundle?.promptTemplateId ?? null,
          initialLlmOutputTemplateId: bundle?.outputTemplateId ?? null,
        },
      });
    },
    [appointmentId, bundles, tenantId, updateBundleMutation],
  );

  const clearToDefault = useCallback(() => {
    return updateBundleMutation({
      variables: {
        tenantId: tenantId!,
        id: appointmentId,
        initialTemplateBundleId: null,
        initialPromptTemplateId: null,
        initialLlmOutputTemplateId: null,
      },
    });
  }, [appointmentId, tenantId, updateBundleMutation]);

  const hasComputedDefault =
    effectiveComputedDefaultBundleId !== null &&
    computedDefaultBundleName !== null;

  const value = useMemo<AppointmentBundleContextValue>(
    () => ({
      storedBundleId,
      computedDefaultBundleName,
      hasComputedDefault,
      bundles,
      loading,
      updateTemplateBundleId,
      clearToDefault,
    }),
    [
      storedBundleId,
      computedDefaultBundleName,
      hasComputedDefault,
      bundles,
      loading,
      updateTemplateBundleId,
      clearToDefault,
    ],
  );

  return (
    <AppointmentBundleContext.Provider value={value}>
      {children}
    </AppointmentBundleContext.Provider>
  );
};
